import { UserBadge } from '../lib/supabase';

interface BadgeProps {
  badge: UserBadge;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  className?: string;
}

export default function Badge({ badge, size = 'medium', showTooltip = true, className = '' }: BadgeProps) {
  const sizeClasses = {
    small: 'w-8 h-8 text-sm',
    medium: 'w-12 h-12 text-base',
    large: 'w-16 h-16 text-lg'
  };

  const rarityStyles = {
    common: {
      border: 'border-amber-600',
      background: 'bg-gradient-to-br from-amber-100 to-amber-200',
      glow: 'shadow-amber-500/20'
    },
    uncommon: {
      border: 'border-gray-400',
      background: 'bg-gradient-to-br from-gray-100 to-gray-200',
      glow: 'shadow-gray-400/20'
    },
    rare: {
      border: 'border-yellow-500',
      background: 'bg-gradient-to-br from-yellow-100 to-yellow-200',
      glow: 'shadow-yellow-500/30'
    },
    legendary: {
      border: 'border-purple-500',
      background: 'bg-gradient-to-br from-purple-100 to-purple-200',
      glow: 'shadow-purple-500/40'
    }
  };

  const badgeStyle = rarityStyles[badge.badge_rarity];

  return (
    <div 
      className={`
        ${sizeClasses[size]}
        ${badgeStyle.border}
        ${badgeStyle.background}
        ${badgeStyle.glow}
        border-2 rounded-full
        flex items-center justify-center
        shadow-lg hover:shadow-xl
        transition-all duration-300
        cursor-pointer
        relative
        ${className}
      `}
      title={showTooltip ? `${badge.badge_name}: ${badge.badge_description}` : undefined}
    >
      <span className="select-none">
        {badge.badge_icon}
      </span>
      
      {/* Rarity indicator */}
      {badge.badge_rarity === 'legendary' && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full animate-pulse" />
      )}
      
      {badge.badge_rarity === 'rare' && (
        <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-yellow-500 rounded-full" />
      )}
    </div>
  );
}

// Badge display component for profile pages
export function BadgeDisplay({ badges }: { badges: UserBadge[] }) {
  if (badges.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <p>No badges earned yet</p>
        <p className="text-sm">Start checking in, creating events, and connecting to earn your first badge!</p>
      </div>
    );
  }

  // Group badges by category
  const badgesByCategory = badges.reduce((acc, badge) => {
    if (!acc[badge.badge_category]) {
      acc[badge.badge_category] = [];
    }
    acc[badge.badge_category].push(badge);
    return acc;
  }, {} as Record<string, UserBadge[]>);

  const categoryNames = {
    checkins: 'Check-in Mastery',
    events: 'Event Leadership', 
    connections: 'Social Engagement',
    attendance: 'Event Participation',
    milestone: 'Milestones'
  };

  return (
    <div className="space-y-4">
      {Object.entries(badgesByCategory).map(([category, categoryBadges]) => (
        <div key={category} className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
            {categoryNames[category as keyof typeof categoryNames] || category}
          </h4>
          <div className="flex flex-wrap gap-2">
            {categoryBadges.map((badge) => (
              <Badge 
                key={badge.badge_id} 
                badge={badge} 
                size="medium"
                showTooltip={true}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Badge progress component
export function BadgeProgress({ badge, currentCount, targetCount }: { 
  badge: UserBadge; 
  currentCount: number; 
  targetCount: number; 
}) {
  const progress = Math.min((currentCount / targetCount) * 100, 100);
  
  return (
    <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
      <div className="flex items-center gap-3">
        <Badge badge={badge} size="small" showTooltip={false} />
        <div className="flex-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{badge.badge_name}</span>
            <span className="text-gray-400">{currentCount}/{targetCount}</span>
          </div>
          <div className="mt-1 bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
